import { yupResolver } from '@hookform/resolvers/yup';
import { CarrierRequestStatus } from '@zorro/clients';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Stack } from '@zorro/web/design-system';
import { RecordForActions } from '@zorro/web/shared-ui';
import { useBatchCallEndpoint, useForm } from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import * as yup from 'yup';

import { ChangeCarrierRequestStatusInput } from '../../ChangeCarrierRequestStatusInput/ChangeCarrierRequestStatusInput';
import { FormFooter } from '../../FormFooter';
import { ErrorsTable } from '../Errors/ErrorsTable';

const carrierRequestStatusSchema = yup.object({
  carrierRequestStatus: yup
    .mixed<CarrierRequestStatus>()
    .oneOf(
      Object.values(CarrierRequestStatus).filter(
        (status) => status !== CarrierRequestStatus.NOT_APPLICABLE
      )
    )
    .required(),
});

type CarrierRequestStatusFormFields = yup.InferType<
  typeof carrierRequestStatusSchema
>;

type Props = {
  recordsForActions: RecordForActions[];
  onClose: () => void;
  onSuccess: () => void;
};

export const BulkChangeCarrierRequestStatusForm = ({
  recordsForActions,
  onClose,
  onSuccess,
}: Props) => {
  const { executeBatchCall, errors } = useBatchCallEndpoint({
    methodName: 'majorMedicalControllerPatchMajorMedicalPlan',
    singularItemName: 'enrollment record',
    action: 'updated',
    batchSize: 5,
  });

  const {
    control,
    formState: { isValid },
    getValues,
  } = useForm<CarrierRequestStatusFormFields>({
    mode: 'all',
    resolver: yupResolver(carrierRequestStatusSchema),
  });

  async function handleSubmit() {
    const { carrierRequestStatus } = getValues();

    const isConfirmedByEmployee =
      carrierRequestStatus === CarrierRequestStatus.CONFIRMED_BY_EMPLOYEE;

    const batchItems = recordsForActions
      .filter(({ benefitId }) => benefitId)
      .map(({ benefitId }) => {
        return {
          key: benefitId!,
          params: [
            benefitId!,
            { isSpecialRequestsConfirmed: isConfirmedByEmployee },
          ] as const,
        };
      });

    await executeBatchCall(batchItems, { onSuccess });
  }

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Save"
        primaryButtonProps={{ onClick: handleSubmit, disabled: !isValid }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{
          onClick: onClose,
        }}
      />
    ),
    [handleSubmit, isValid, onClose]
  );
  useDrawerFooter(drawerFooter);

  return (
    <Stack>
      <ChangeCarrierRequestStatusInput
        control={control}
        name="carrierRequestStatus"
        selectProps={{ searchable: false }}
      />

      <ErrorsTable
        tableTitle="Enrollment record"
        errors={errors}
        isBulk={recordsForActions.length > 1}
      />
    </Stack>
  );
};
